import type { Payout } from '../types/payout';
import type { DeclarationFrequency } from '../types/settings';

export interface DeclarationPeriod {
  label: string;
  startDate: Date;
  endDate: Date;
}

export const getCurrentDeclarationPeriod = (frequency: DeclarationFrequency): DeclarationPeriod => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based
  const currentQuarter = Math.floor(currentMonth / 3);

  switch (frequency) {
    case 'annuel':
      return {
        label: `Année ${currentYear}`,
        startDate: new Date(currentYear, 0, 1), // 1er janvier
        endDate: new Date(currentYear, 11, 31, 23, 59, 59) // 31 décembre
      };

    case 'trimestriel':
      const quarterStartMonth = currentQuarter * 3;
      return {
        label: `T${currentQuarter + 1} ${currentYear}`,
        startDate: new Date(currentYear, quarterStartMonth, 1),
        endDate: new Date(currentYear, quarterStartMonth + 3, 0, 23, 59, 59) // Dernier jour du trimestre
      };

    case 'mensuel':
      // Pour mensuel : uniquement le mois en cours
      return {
        label: `${getMonthName(currentMonth)} ${currentYear}`,
        startDate: new Date(currentYear, currentMonth, 1),
        endDate: new Date(currentYear, currentMonth + 1, 0, 23, 59, 59) // Dernier jour du mois courant
      };

    default:
      // Fallback sur trimestriel mais ne devrait jamais arriver
      const fallbackQuarterStart = currentQuarter * 3;
      return {
        label: `T${currentQuarter + 1} ${currentYear}`,
        startDate: new Date(currentYear, fallbackQuarterStart, 1),
        endDate: new Date(currentYear, fallbackQuarterStart + 3, 0, 23, 59, 59)
      };
  }
};

export const filterPayoutsForDeclaration = (payouts: Payout[], frequency: DeclarationFrequency): Payout[] => {
  const period = getCurrentDeclarationPeriod(frequency);
  
  return payouts.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate >= period.startDate && payoutDate <= period.endDate;
  });
};

export const calculateDeclarationTotal = (payouts: Payout[], frequency: DeclarationFrequency): number => {
  const filteredPayouts = filterPayoutsForDeclaration(payouts, frequency);
  return filteredPayouts.reduce((sum, payout) => sum + (payout.totalValue || 0), 0);
};

const getMonthName = (monthIndex: number): string => {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months[monthIndex];
};